/**
 * A content stream: one article collection, one tag collection it alone owns,
 * and the key the consuming app hangs its URL segment and i18n off.
 *
 * **Only the Firestore paths live here.** The URL segment belongs to
 * `apps/one-ai` alone and stays in its `lib/seo/config.ts`, the same split
 * `routePaths` already uses — the paths earn promotion to the shared package
 * because both apps need them, the segment does not.
 */
export type StreamKey = "blog" | "news";

export interface ContentStream {
    key: StreamKey;
    /** Firestore path — articles. */
    collection: string;
    /** Firestore path — tags. Owned by this stream alone. */
    tagCollection: string;
}

/**
 * The original One AI stream, migrated out of `news` on 2026-09-16 so the new
 * stream could take that name, and renamed from NOTES to BLOG on 2026-09-17.
 * Its documents kept their ids, so `/blog/<id>` serves what `/news/<id>` used
 * to.
 *
 * **Neither Firestore path was renamed with the key**, and that is the same
 * trade both times. An article stores its tags as Firestore
 * `DocumentReference`s (`apps/cms/src/fields/TagChipsField.tsx` writes
 * `getReferenceFrom`), so the stored value carries the collection path:
 * renaming `tags` would mean rewriting the `tags` array on every article, and
 * renaming `notes` would mean copying every document and rewriting every
 * reference that points into it. The cost of not renaming is that `notes` and
 * `tags` permanently mean "the blog ones"; that is the cheaper half of the
 * trade, and the reason `key` — never a path string — is what gets threaded
 * through the app.
 */
export const BLOG_STREAM: ContentStream = {
    key: "blog",
    collection: "notes",
    tagCollection: "tags",
};

/** The stream created on 2026-09-16, reusing the name the old one vacated. */
export const NEWS_STREAM: ContentStream = {
    key: "news",
    collection: "news",
    tagCollection: "news-tags",
};

/**
 * Every stream, for the consumers that must cover all of them: the CMS's
 * collection registry and its tag-search controller.
 */
export const streams: readonly ContentStream[] = [BLOG_STREAM, NEWS_STREAM];
